// ─────────────────────────────────────────────────────────────────────────────
// API do MySuite — tickets, interações, solicitantes e a fila de pendentes.
//
// Tudo que na versão 1.x era `fetch(MYSUITE_API_BASE + ...)` espalhado pelo
// renderer está reunido aqui. Nenhuma função toca a tela: elas recebem
// parâmetros e devolvem dados. Quem exibe é a view.
// ─────────────────────────────────────────────────────────────────────────────

import { MYSUITE_API_BASE } from '../config/ambiente';
import { tabelaOperador } from './supabase';
import { STATUS_FILA_RETORNO } from './filaRetorno';
import { decidirAssumir, donoDoTicket } from './assumirTicket';
import { decidirEmAtendimento, alteracaoMaisRecente, FUSO_MYSUITE } from './ociosidade';
import { codigoDoStatusTexto } from './painelGerencia';

// Fila genérica do suporte. Ticket nela não tem dono de fato.
export const OPERADOR_ANALISE_SUPORTE = 106;

// Status "Sup - Em Atendimento" e setor Suporte, usados na lista de atendimentos.
export const STATUS_EM_ATENDIMENTO = 36;
export const SETOR_SUPORTE = 3;

// O fuso em que o MySuite grava data e hora mora em services/ociosidade.js,
// junto da regra que o usa. Reexportado aqui porque este é o arquivo que fala
// com a API — quem importa a API não precisa saber onde a constante nasceu.
export { FUSO_MYSUITE };

// Os outros status que a tela de início mostra. Os números são os mesmos de
// codigosMysuite.js — repetidos aqui de propósito, para este arquivo continuar
// consultável sem depender do módulo de regras da formalização. A fila de
// retorno (5) é a exceção: ela tem casa própria em filaRetorno.js, com as
// regras de prazo, e o número vem importado de lá.
export const STATUS_SETOR_SISTEMAS = 18;   // encaminhado ao Setor de Sistemas
// Aparece pouco e quase sempre é reabertura de atendimento: quando aparece,
// passa na frente das outras filas.
export const STATUS_AGUARDANDO_SOLUCAO = 1;

/** Dados de um ticket. null quando não encontrado. */
export async function buscarTicket(numero) {
  const num = String(numero || '').trim();
  if (!num) return null;
  const res = await fetch(`${MYSUITE_API_BASE}/tickets/${num}`);
  if (!res.ok) throw new Error('Ticket não encontrado');
  return await res.json();
}

// Observação do cliente por CNPJ. Muda pouco e é lida a cada ticket do mesmo
// cliente — guarda a resposta da sessão em vez de repetir a consulta.
const observacoesPorCnpj = new Map();

/**
 * O campo `observacao` do cadastro do cliente (`/dados/clientecnpj`): dados de
 * conexão, acessos, particularidades. Texto puro, com `\r` como quebra de
 * linha — devolvido já com `\n`.
 *
 * '' quando o cliente não tem observação ou não está cadastrado (a API
 * responde 404 "Não encontrado dados"); `null` quando a consulta falhou, para
 * a tela poder dizer "tente de novo" em vez de "não há nada".
 */
export async function observacaoDoCliente(cnpj) {
  const c = String(cnpj || '').trim();
  if (!c) return '';
  if (observacoesPorCnpj.has(c)) return observacoesPorCnpj.get(c);
  try {
    const res = await fetch(`${MYSUITE_API_BASE}/dados/clientecnpj?cnpj=${encodeURIComponent(c)}`);
    if (res.status === 404) { observacoesPorCnpj.set(c, ''); return ''; }
    if (!res.ok) throw new Error(`O MySuite respondeu ${res.status}`);
    const dados = await res.json();
    const texto = String(dados?.observacao || '').replace(/\r\n?/g, '\n').trim();
    observacoesPorCnpj.set(c, texto);
    return texto;
  } catch (err) {
    console.warn('[Orion] Observação do cliente:', err.message);
    return null;
  }
}

/**
 * Interações de um ticket. Quando `primeiraInteracao` é informada (ela só vem
 * em /tickets/{num}, não no endpoint de interações), entra no topo da lista —
 * a não ser que já esteja lá.
 */
export async function buscarInteracoes(numero, primeiraInteracao) {
  let interacoes = [];
  try {
    const res = await fetch(`${MYSUITE_API_BASE}/tickets/interacoes/${numero}`);
    interacoes = res.ok ? await res.json() : [];
  } catch (e) {
    interacoes = [];
  }

  const primeira = (primeiraInteracao || '').trim();
  const jaPresente = interacoes.some(it => (it.descricao || '').trim() === primeira);
  if (primeira && !jaPresente) {
    interacoes.unshift({
      dataInteracao: 'Abertura do ticket',
      horaInteracao: '',
      operador: '',
      descricao: primeira,
      privado: 0,
    });
  }

  return interacoes;
}

// ── Solicitantes ─────────────────────────────────────────────────────────────
// O endpoint /dados/clientes/usuarios devolve TODOS os clientes e usuários de
// uma vez. Carrega sob demanda e reutiliza pelo resto da sessão.

let clientesEUsuariosCache = null;
let carregandoClientesEUsuarios = null;

/** Busca crua de /dados/clientes/usuarios, cacheada — fonte comum das duas funções abaixo. */
async function obterClientesEUsuariosBruto() {
  if (clientesEUsuariosCache) return clientesEUsuariosCache;

  if (!carregandoClientesEUsuarios) {
    carregandoClientesEUsuarios = (async () => {
      const res = await fetch(`${MYSUITE_API_BASE}/dados/clientes/usuarios`);
      if (!res.ok) throw new Error('Falha ao carregar usuários dos clientes');
      const clientes = await res.json();
      clientesEUsuariosCache = Array.isArray(clientes) ? clientes : [];
      return clientesEUsuariosCache;
    })().finally(() => { carregandoClientesEUsuarios = null; });
  }

  return carregandoClientesEUsuarios;
}

export async function obterMapaUsuariosCliente() {
  const clientes = await obterClientesEUsuariosBruto();
  const mapa = new Map();
  clientes.forEach(c => {
    (c.usuarios || []).forEach(u => {
      if (u && u.codigo != null) mapa.set(Number(u.codigo), u.nome);
    });
  });
  return mapa;
}

/**
 * Clientes com seus usuários, para os selects em cascata do filtro avançado
 * de tickets (codigoCliente → codigoUsuarioCliente).
 * Devolve `[{ codigo, nome, usuarios: [{ codigo, nome }] }]`. O nome do
 * campo de código/nome do cliente varia conforme a resposta do MySuite — daí
 * a leitura defensiva, no mesmo padrão do CNPJ em `validarTicket`.
 */
export async function obterClientesComUsuarios() {
  const clientes = await obterClientesEUsuariosBruto();
  return clientes.map(c => ({
    codigo: c.codigo ?? c.codigoCliente ?? c.cliente ?? null,
    nome: c.nome ?? c.nomeCliente ?? c.razaoSocial ?? '',
    usuarios: (c.usuarios || []).map(u => ({ codigo: u.codigo, nome: u.nome })),
  })).filter(c => c.codigo != null);
}

/**
 * Código do solicitante de um ticket, direto da API.
 * Usado pelo "criar novo ticket" para herdar o solicitante do ticket de origem
 * sem depender do que estiver em memória — que pode ter sido limpo, ou ter
 * vindo de um caminho (fila de pendentes, cache) que não trouxe o campo.
 */
export async function codigoSolicitanteDoTicket(numero) {
  const num = String(numero || '').trim();
  if (!num) return null;
  try {
    const res = await fetch(`${MYSUITE_API_BASE}/tickets/${num}`);
    if (!res.ok) return null;
    const data = await res.json();
    return parseInt(data.codigoUsuarioCliente, 10) || null;
  } catch (e) { return null; }
}

/** Nome do solicitante a partir do código. '' quando não achar. */
export async function nomeSolicitante(codigo) {
  if (!codigo) return '';
  try {
    const mapa = await obterMapaUsuariosCliente();
    return mapa.get(Number(codigo)) || '';
  } catch (e) { return ''; }
}

/**
 * Resolve o solicitante de um ticket em uma tacada:
 * /tickets/{num} → codigoUsuarioCliente → nome.
 * Devolve '' quando o ticket não existe ou o código não tem nome.
 */
export async function resolverSolicitantePorTicket(numero) {
  const cod = await codigoSolicitanteDoTicket(numero);
  if (!cod) return '';
  return await nomeSolicitante(cod);
}

// ── Fila de pendentes ────────────────────────────────────────────────────────
// A fila de pendentes traz o dono como `codigoOperador` e o status como CÓDIGO
// (`codigoStatusTicket`) — é a única leitura com o status em número. O mapa
// abaixo evita repetir a consulta.
//
// Ela NÃO é mais a única fonte do dono: conferido na API em 28/08/2026,
// `/tickets/{num}` também devolve `operador` (o ticket 348745 volta com
// `operador: 115`), e essa leitura é mais fresca — a fila leva alguns minutos
// para refletir uma mudança de status. Quem precisa saber de quem é o ticket
// AGORA usa o detalhe; a fila continua valendo como reserva.
//
// ⚠️ O MAPA TEM VALIDADE. Até a 2.0.3 ele era preenchido na primeira consulta
// e nunca mais renovado: o ticket já visto ficava com o dono daquele momento
// pelo resto da sessão. Em 22/10/2026 o 352067 esteve com Caio Cunha das 09:18
// às 09:34, voltou para a Análise Suporte e, às 09:56, a Joyce Almeida foi
// avisada de que ele "está em atendimento com CAIO CUNHA" — o mapa ainda era o
// de quando ela tinha aberto outro ticket, mais cedo. Agora o mapa é TROCADO
// inteiro a cada leitura (ticket que saiu da fila sai do mapa) e vale por
// `VALIDADE_FILA_MS`; quem decide de quem é o ticket pede a leitura fresca.

let mapaTicketsPendentes = {};
let filaLidaEm = 0;
const VALIDADE_FILA_MS = 30 * 1000;

// Tickets que ESTE Orion acabou de assumir. A fila só mostra a mudança alguns
// minutos depois; sem este registro, a leitura fresca devolveria o status
// antigo e um segundo clique no mesmo card repetiria o POST. Vale só enquanto
// a API pode estar atrasada — depois disso, quem manda é a fila.
const marcacoesLocais = new Map();   // número → { operador, em }
const VALIDADE_MARCACAO_MS = 3 * 60 * 1000;

function aplicarMarcacaoLocal(t) {
  const m = marcacoesLocais.get(String(t.codigo));
  if (!m) return;
  if (Date.now() - m.em > VALIDADE_MARCACAO_MS) { marcacoesLocais.delete(String(t.codigo)); return; }
  t.codigoStatusTicket = STATUS_EM_ATENDIMENTO;
  t.codigoSetorTicket = SETOR_SUPORTE;
  t.codigoOperador = m.operador;
}

/** Fila de pendentes do suporte (lista completa). */
export async function buscarPendentes(filtro = {}) {
  const res = await fetch(`${MYSUITE_API_BASE}/dashboard/suporte/ticket/pendentes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(filtro),
  });
  if (!res.ok) throw new Error('Falha ao carregar a fila de pendentes');
  const tickets = await res.json();
  const lista = Array.isArray(tickets) ? tickets : [];

  // Só a fila COMPLETA substitui o mapa: uma consulta filtrada traz parte dela
  // e apagaria tickets que continuam lá.
  const completa = !filtro || !Object.keys(filtro).length;
  const mapa = completa ? {} : mapaTicketsPendentes;
  lista.forEach(t => {
    if (t && t.codigo != null) { aplicarMarcacaoLocal(t); mapa[String(t.codigo)] = t; }
  });
  if (completa) { mapaTicketsPendentes = mapa; filaLidaEm = Date.now(); }

  return lista;
}

/**
 * Ticket da fila de pendentes (com codigoOperador). Consulta a fila quando o
 * mapa passou da validade.
 *
 * `fresco: true` é para quem vai DECIDIR algo pelo dono (assumir, acusar ticket
 * de outro): sempre relê a fila, e se a leitura falhar devolve null — "não
 * sei" — em vez de um dono velho.
 */
export async function ticketDaFila(numero, { fresco = false } = {}) {
  const valido = Date.now() - filaLidaEm < VALIDADE_FILA_MS;
  const doCache = mapaTicketsPendentes[String(numero)];
  if (!fresco && valido && doCache) return doCache;
  try {
    await buscarPendentes({});
    return mapaTicketsPendentes[String(numero)] || null;
  } catch (e) {
    return fresco ? null : (doCache || null);
  }
}

/** Cache da fila, para quem precisa reaproveitar códigos ao criar um ticket novo. */
export function obterMapaPendentes() {
  return mapaTicketsPendentes;
}

/** Nome do operador a partir do código (orion.operador). '' quando não achar. */
export async function nomeOperadorPorCodigo(codigo) {
  if (!Number.isFinite(codigo)) return '';
  try {
    const { data, error } = await (await tabelaOperador())
      .select('ope_nome, ope_sobrenome')
      .eq('ope_codigo', codigo)
      .maybeSingle();
    if (error || !data) return '';
    return `${data.ope_nome || ''} ${data.ope_sobrenome || ''}`.trim();
  } catch (e) { return ''; }
}

/**
 * Verifica se o ticket está em atendimento com OUTRO operador.
 * Devolve { deOutro: true, dono, nomeDono } ou { deOutro: false }.
 *
 * Silencioso (deOutro: false) quando:
 *   · o ticket é do próprio operador logado;
 *   · está na fila de Análise Suporte (sem dono);
 *   · não dá para saber o dono (ticket fora da fila, ex.: já concluído);
 *   · o detalhe do ticket (`detalhe`, o `/tickets/{n}` que a tela já leu)
 *     aponta outro dono — as duas leituras discordando é sinal de mudança
 *     recente, e acusar um colega por engano é pior do que não avisar.
 */
export async function verificarDonoDoTicket(numero, meuCodigo, detalhe = null) {
  if (!meuCodigo) return { deOutro: false };     // sem cadastro, não há com o que comparar
  try {
    const pend = await ticketDaFila(numero, { fresco: true });
    const dono = pend && pend.codigoOperador;
    if (!Number.isFinite(dono)) return { deOutro: false };  // dono desconhecido: melhor não palpitar
    if (dono === meuCodigo) return { deOutro: false };
    if (dono === OPERADOR_ANALISE_SUPORTE) return { deOutro: false };
    const doDetalhe = Number(detalhe?.operador);
    if (detalhe && Number.isFinite(doDetalhe) && doDetalhe !== dono) return { deOutro: false };

    return { deOutro: true, dono, nomeDono: await nomeOperadorPorCodigo(dono) };
  } catch (err) {
    // Aviso é auxiliar: se falhar, o atendimento segue normalmente.
    console.warn('[Orion] Aviso de dono do ticket:', err.message);
    return { deOutro: false };
  }
}

/**
 * Assume o ticket no MySuite: status "Sup - Em Atendimento" (36) no nome do
 * operador informado.
 *
 * Vai direto do Orion para `/tickets/atualizar` — passar pelo n8n só para
 * repassar três chaves adicionaria um salto sem ganho.
 *
 * **`codigoStatus` é o campo que faz a coisa acontecer.** O endpoint atualiza
 * o que recebe e MANTÉM o que não recebe, então um POST sem ele responde
 * "Ticket Atualizado" e não muda nada — foi exatamente o que aconteceu na
 * primeira versão desta função. `codigoOperador` vai junto porque é ele quem
 * tira o ticket da fila genérica (106) e o põe no nome de quem clicou.
 *
 * Nada mais é enviado, de propósito: previsão, criticidade e classificação
 * ficam como estão. Atender não é editar o ticket.
 *
 * A resposta NÃO é JSON: o MySuite devolve o texto "Ticket Atualizado" — e o
 * devolve mesmo quando ignora o pedido, então ela não serve de confirmação.
 *
 * O efeito não aparece na hora: a leitura da API leva alguns minutos para
 * mostrar o novo status, e as filas do Orion recarregam a cada 5.
 */
export async function marcarEmAtendimento(codigoTicket, codigoOperador) {
  const res = await fetch(`${MYSUITE_API_BASE}/tickets/atualizar`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      codigoTicket: Number(codigoTicket),
      codigoStatus: STATUS_EM_ATENDIMENTO,
      codigoOperador: Number(codigoOperador),
    }),
  });
  if (!res.ok) throw new Error(`O MySuite respondeu ${res.status}`);
  return (await res.text()).trim();
}

/**
 * O ticket está em "Sup - Em Atendimento" AGORA?
 *
 * Existe porque a resposta do POST não serve de confirmação: `/tickets/atualizar`
 * devolve 201 "Ticket Atualizado" mesmo quando ignora o pedido. Quem confirma é
 * o detalhe do ticket, que reflete a escrita em segundos — ao contrário da fila
 * de pendentes, que leva alguns minutos.
 *
 * O código do status nem sempre vem no detalhe (ver a doc da API: os nomes
 * mudam de endpoint para endpoint), por isso o texto de `status` também vale.
 *
 * O detalhe NÃO reflete a escrita sozinho: ele lê uma tabela que o MySuite
 * só recarrega em ciclos. Medido no 347291 em 22/10/2026, a escrita das
 * 08:41:11 só apareceu em /tickets/{n} às 08:42:14 — e a das 08:38:06, às
 * 08:40:00. Por isso, antes de ler, a conferência pede a carga do dia
 * (`dispararCargaDoDia`) e relê com intervalos crescentes, por até ~20 s.
 */
const ESPERAS_CONFIRMACAO_MS = [0, 1500, 2500, 4000, 5000, 7000];

/** Hoje no fuso do MySuite, no formato da API: dd/MM/aaaa. */
function hojeNoMysuite() {
  const horas = Number(FUSO_MYSUITE.slice(0, 3));
  const d = new Date(Date.now() + horas * 3600 * 1000);
  const pad = n => String(n).padStart(2, '0');
  return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
}

/**
 * Pede ao MySuite que recarregue a tabela de tickets do dia — a mesma que
 * `/tickets/{n}` lê. A resposta sai na hora ("Dados sendo inseridos na tabela
 * ticket em segundo plano") e a carga corre depois; por isso quem chama
 * continua relendo o detalhe. Falhar aqui não impede a conferência.
 */
export async function dispararCargaDoDia() {
  const hoje = hojeNoMysuite();
  try {
    await fetch(`${MYSUITE_API_BASE}/tickets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // false: a carga NÃO pode se limitar aos tickets abertos hoje — o ticket
      // atendido quase nunca é de hoje (o 347291 é de agosto).
      body: JSON.stringify({ dataInicial: hoje, dataFinal: hoje, consideraDtAbertura: false }),
    });
  } catch (err) {
    console.warn('[Orion] Carga de tickets do dia:', err.message);
  }
}

async function confirmouEmAtendimento(numero) {
  await dispararCargaDoDia();
  for (const espera of ESPERAS_CONFIRMACAO_MS) {
    // eslint-disable-next-line no-await-in-loop
    if (espera) await new Promise(r => setTimeout(r, espera));
    try {
      // eslint-disable-next-line no-await-in-loop
      const d = await buscarTicket(numero);
      const cod = Number(d?.codigoStatus ?? d?.codigoStatusTicket);
      if (cod === STATUS_EM_ATENDIMENTO) return true;
      if (/em\s+atendimento/i.test(String(d?.status || ''))) return true;
    } catch (e) { /* rede instável: a próxima tentativa resolve */ }
  }
  return false;
}

/**
 * Decide se o clique em "Atender" deve assumir o ticket — e assume, quando deve.
 *
 * A regra é conservadora de propósito: o Orion só mexe no status de um ticket
 * que **não tem dono de fato**. Assumir o ticket de um colega pelo MySuite é
 * tirá-lo da fila dele sem que ele saiba.
 *
 * Não assume quando:
 *   · o operador logado não tem código em `orion.operador` — não há em nome de
 *     quem marcar;
 *   · o ticket já está em atendimento — com o próprio operador (não há o que
 *     marcar) ou com outra pessoa;
 *   · o dono é OUTRO operador (e não a fila genérica de Análise Suporte);
 *   · não dá para saber o estado do ticket (fora da fila de pendentes) — o
 *     mesmo critério de `verificarDonoDoTicket`: sem informação, não palpita.
 *
 * `dica` é o registro que a tela já tem em mãos — o item da lista, vindo de
 * `/tickets/filtro`. Ele existe porque a fila de pendentes NÃO devolve tickets
 * no Setor de Sistemas (status 18): sem a dica, todo ticket daquele cartão
 * seria recusado como "desconhecido". A lista é consultada pelo código do
 * operador logado, então o dono que ela traz é confiável.
 *
 * Devolve `{ assumiu, motivo }` e, quando o ticket é de outro colega, também
 * `dono` e `nomeDono` — quem recusa precisa dizer para quem ligar. Nunca lança:
 * quem chama trata a recusa, não uma exceção.
 */
export async function assumirTicketAoAtender(numero, codigoOperador, dica = null) {
  const meu = Number(codigoOperador);
  const num = Number(String(numero || '').replace(/\D/g, ''));
  if (!num) return { assumiu: false, motivo: 'sem-ticket' };

  try {
    // A fila de pendentes é a fonte preferida: é a única que traz o dono com
    // status do MySuite. Quando ela não conhece o ticket, vale o que a tela viu.
    // Fresca: decidir pelo dono de minutos atrás é recusar um ticket que já
    // está livre (ou assumir um que acabou de ganhar dono).
    const pend = (await ticketDaFila(num, { fresco: true })) || dica;

    const { pode, motivo } = decidirAssumir(pend, meu);
    if (!pode) {
      // Recusa por causa de outra pessoa: quem recusa precisa dizer para quem
      // ligar, então o nome do dono vai junto.
      if (motivo === 'de-outro' || motivo === 'ja-de-outro') {
        const dono = donoDoTicket(pend);
        return { assumiu: false, motivo, dono, nomeDono: await nomeOperadorPorCodigo(dono) };
      }
      return { assumiu: false, motivo };
    }

    await marcarEmAtendimento(num, meu);

    // Confere no MySuite. Sem isto o Orion anunciaria sucesso em cima de um 201
    // que sai mesmo quando nada foi gravado — que foi como o primeiro formato
    // do payload passou despercebido.
    if (!await confirmouEmAtendimento(num)) return { assumiu: false, motivo: 'nao-gravou' };

    // O cache da fila de pendentes ainda diz o status antigo, e a API só
    // reflete a mudança minutos depois. Sem esta correção, clicar duas vezes no
    // mesmo ticket dispararia o POST de novo. A marcação local cobre as
    // próximas leituras da fila enquanto ela não alcança o MySuite.
    marcacoesLocais.set(String(num), { operador: meu, em: Date.now() });
    if (pend) {
      pend.codigoStatusTicket = STATUS_EM_ATENDIMENTO;
      pend.codigoSetorTicket = SETOR_SUPORTE;
      pend.codigoOperador = meu;
      // O item de `/tickets/filtro` guarda o status como TEXTO, não como
      // código: sem atualizar os dois, a mesma tela leria estados diferentes.
      if (pend.status) pend.status = 'SUP - Em Atendimento';
      if (pend.operador != null) pend.operador = meu;
    }

    return { assumiu: true, motivo: 'ok' };
  } catch (err) {
    console.warn('[Orion] Falha ao assumir o ticket:', err.message);
    return { assumiu: false, motivo: 'falha' };
  }
}

/**
 * Tickets do operador em um status, por `POST /tickets/filtro`.
 *
 * É a consulta que sustenta todos os cartões da tela de início. Devolve `null`
 * (e não lista vazia) quando a consulta falha: são coisas diferentes — vazio
 * significa "não há ticket neste status" e o cartão pode zerar com segurança;
 * null significa "não sei", e aí ele mantém o que já tinha em vez de anunciar
 * uma fila limpa que talvez não esteja.
 */
export async function ticketsPorStatus(codigoOperador, codigoStatus) {
  if (!codigoOperador) return null;
  try {
    const res = await fetch(`${MYSUITE_API_BASE}/tickets/filtro`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        codigoOperador: Number(codigoOperador),
        codigoStatus,
      }),
    });
    if (!res.ok) return null;
    const dados = await res.json();
    const lista = Array.isArray(dados) ? dados : (dados?.tickets || dados?.dados || []);
    return Array.isArray(lista) ? lista : null;
  } catch (err) {
    console.warn(`[Orion] Tickets no status ${codigoStatus}:`, err.message);
    return null;
  }
}

/**
 * Campos aceitos por `POST /tickets/filtro`, na tela de consulta avançada.
 * Nenhum é obrigatório: o operador escolhe qualquer combinação.
 */
const CAMPOS_FILTRO_TICKET = [
  'codigoOperador', 'codigoStatus', 'dataUltimaAlteracao', 'codigoCliente',
  'codigoUsuarioCliente', 'codigoClassificacao', 'codigoCategoria',
  'codigoSetor', 'dataAbertura',
];

/**
 * Filtro avançado de tickets — a mesma consulta de `ticketsPorStatus`, mas
 * com todos os campos que o MySuite aceita em `/tickets/filtro`, para a tela
 * de consulta (não só o que sustenta os cartões da tela de início).
 *
 * Só entram no corpo os campos INFORMADOS: mandar `codigoStatus: 0` filtraria
 * por um status que não existe, em vez de "qualquer status" — por isso os
 * campos vazios, `null` ou `0` ficam de fora, e não zerados.
 */
export async function filtrarTickets(filtro = {}) {
  const corpo = {};
  CAMPOS_FILTRO_TICKET.forEach(campo => {
    const v = filtro[campo];
    if (v === undefined || v === null || v === '' || v === 0) return;
    corpo[campo] = v;
  });

  try {
    const res = await fetch(`${MYSUITE_API_BASE}/tickets/filtro`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(corpo),
    });
    if (!res.ok) return null;
    const dados = await res.json();
    const lista = Array.isArray(dados) ? dados : (dados?.tickets || dados?.dados || []);
    return Array.isArray(lista) ? lista : null;
  } catch (err) {
    console.warn('[Orion] Filtro avançado de tickets:', err.message);
    return null;
  }
}

/** Tickets em "Sup - Em Atendimento" do operador informado. */
export async function meusAtendimentos(codigoOperador) {
  return (await ticketsPorStatus(codigoOperador, STATUS_EM_ATENDIMENTO)) || [];
}

/**
 * As três filas da tela de início, uma consulta por status, em paralelo.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * POR QUE NÃO SAI MAIS DA FILA DE PENDENTES
 *
 * Saía, e estava errado por dois motivos independentes — os dois descobertos
 * conferindo a resposta real da API, não o código:
 *
 *   1. o filtro lia `t.codigoStatus`, campo que a fila de pendentes NÃO tem.
 *      Ela responde `codigoStatusTicket`. `Number(undefined) === 18` é sempre
 *      falso, então as três filas vinham vazias — e não só a de sistemas;
 *
 *   2. mesmo com o nome certo, não adiantaria para o Setor de Sistemas: a
 *      fila de pendentes só devolve os status 1, 5, 36, 90 e 104. O 18 não
 *      está lá, e nunca estaria.
 *
 * `POST /tickets/filtro` com `codigoStatus` responde certo para todos eles —
 * é o mesmo endpoint que a fila de retorno já usava, e o único que enxerga o
 * status 18. Conferido contra a API: para o operador 115, os status 1 e 36
 * batem número por número com o que a fila de pendentes trazia.
 *
 * São três POSTs em vez de um, mas cada um devolve só o que interessa, em vez
 * da fila inteira do setor para filtrar no cliente.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * A fila de retorno (status 5) NÃO está aqui de propósito: ela tem serviço
 * próprio, com os horários combinados e as regras de prazo — ver
 * `ticketsFilaRetorno` e `services/filaRetorno.js`.
 */
export async function filasDoOperador(codigoOperador) {
  const vazio = { aguardandoSolucao: [], emAtendimento: [], setorSistemas: [], outrosStatus: [] };
  if (!codigoOperador) return vazio;

  const [aguardandoSolucao, emAtendimento, setorSistemas, todos] = await Promise.all([
    ticketsPorStatus(codigoOperador, STATUS_AGUARDANDO_SOLUCAO),
    ticketsPorStatus(codigoOperador, STATUS_EM_ATENDIMENTO),
    ticketsPorStatus(codigoOperador, STATUS_SETOR_SISTEMAS),
    ticketsDoOperador(codigoOperador),
  ]);

  // Uma fila que falhou vira lista vazia aqui, e não null: quem chama guarda o
  // objeto inteiro no estado. O `null` de `ticketsPorStatus` cumpre seu papel
  // em quem consulta um status só.
  return {
    aguardandoSolucao: aguardandoSolucao || [],
    emAtendimento: emAtendimento || [],
    setorSistemas: setorSistemas || [],
    outrosStatus: ticketsEmOutrosStatus(todos || []),
  };
}

/**
 * Todos os tickets do operador, em qualquer status — `POST /tickets/filtro`
 * só com `codigoOperador`. `null` quando a consulta falha, como em
 * `ticketsPorStatus`.
 *
 * É uma resposta pesada: vem o histórico inteiro, concluídos inclusive
 * (conferido em 27/10/2026: 3.760 tickets, ~1,9 MB, meio segundo para o
 * operador 115). Não há como pedir "todos menos o 3" — o filtro aceita um
 * status só —, e consultar status a status deixaria de fora justamente o
 * status que ninguém lembrou de listar, que é o problema que isto resolve.
 */
export async function ticketsDoOperador(codigoOperador) {
  if (!codigoOperador) return null;
  try {
    const res = await fetch(`${MYSUITE_API_BASE}/tickets/filtro`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ codigoOperador: Number(codigoOperador) }),
    });
    if (!res.ok) return null;
    const dados = await res.json();
    const lista = Array.isArray(dados) ? dados : (dados?.tickets || dados?.dados || []);
    return Array.isArray(lista) ? lista : null;
  } catch (err) {
    console.warn('[Orion] Tickets do operador:', err.message);
    return null;
  }
}

/**
 * O cartão "Outros status": tickets no nome do operador que não aparecem em
 * nenhum outro lugar da tela de início — e por isso ficavam no limbo (um
 * "Aguardando interação do cliente" esquecido, por exemplo).
 *
 * `/tickets/filtro` devolve o status como TEXTO, sem o código. Ficam de fora:
 *   · concluído (status 3);
 *   · o que é do Setor de Sistemas — status "SIS - …", o 18 ("Em Análise /
 *     Sistemas") e ticket que já está no setor 5-Sistemas;
 *   · os status que já têm cartão próprio: 1, 5 e 36.
 */
export function ticketsEmOutrosStatus(lista) {
  const JA_TEM_CARTAO = [STATUS_AGUARDANDO_SOLUCAO, STATUS_FILA_RETORNO, STATUS_EM_ATENDIMENTO, STATUS_SETOR_SISTEMAS];
  return (lista || []).filter(t => {
    const status = String(t?.status || '').trim();
    if (!status) return false;
    if (/conclu[íi]d/i.test(status)) return false;
    if (/^sis\s*-/i.test(status)) return false;
    if (/sistemas/i.test(String(t?.setor || ''))) return false;
    return !JA_TEM_CARTAO.includes(codigoDoStatusTexto(status));
  });
}

/**
 * Quantos tickets o operador tem em atendimento AGORA.
 *
 * É a fonte da verdade do monitor de ociosidade: estar atendendo não é mexer no
 * Orion, é ter ticket em status 36. Um operador pode passar meia hora ao
 * telefone com o cliente sem tocar no app — e não está ocioso.
 *
 * Usa `POST /tickets/filtro`, que responde só com o que interessa, em vez da
 * fila inteira de pendentes.
 *
 * Devolve `null` quando não foi possível saber — e quem chama trata isso como
 * "mantém o que sabia antes". Falha de rede não pode marcar ninguém de ocioso.
 */
export async function quantosEmAtendimento(codigoOperador) {
  if (!codigoOperador) return null;
  try {
    const res = await fetch(`${MYSUITE_API_BASE}/tickets/filtro`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        codigoOperador: Number(codigoOperador),
        codigoStatus: STATUS_EM_ATENDIMENTO,
      }),
    });
    if (!res.ok) return null;
    const dados = await res.json();
    const lista = Array.isArray(dados) ? dados : (dados?.tickets || dados?.dados || []);
    return Array.isArray(lista) ? lista.length : null;
  } catch (err) {
    console.warn('[Orion] Consulta de tickets em atendimento:', err.message);
    return null;
  }
}

/**
 * Instante da ÚLTIMA alteração que o operador fez em algum ticket hoje.
 *
 * É a régua da ociosidade. A pergunta que ela responde não é "o operador mexeu
 * no Orion?", e sim "quando foi a última vez que ele tocou em um ticket?" — que
 * é o trabalho de verdade. Dá para passar meia hora ao telefone sem tocar no
 * app, e isso não é ócio; dá para deixar o Orion aberto a tarde inteira sem
 * atender ninguém, e isso é.
 *
 * `POST /tickets/filtro` com `dataUltimaAlteracao` devolve só os tickets
 * mexidos naquele dia — uma dúzia, não os milhares do histórico. Cada um traz
 * `dataAlteracao` + `horaAlteracao`; o mais recente é a resposta.
 *
 * Devolve `Date`, ou `null` quando não deu para saber (falha de rede) OU quando
 * o operador ainda não mexeu em ticket nenhum hoje. Quem chama trata os dois
 * como "não sei" — nunca como "está ocioso desde a meia-noite".
 */
export async function ultimaAlteracaoDoOperador(codigoOperador, dia = new Date()) {
  if (!codigoOperador) return null;

  const pad = n => String(n).padStart(2, '0');
  const data = `${dia.getFullYear()}-${pad(dia.getMonth() + 1)}-${pad(dia.getDate())}`;

  try {
    const res = await fetch(`${MYSUITE_API_BASE}/tickets/filtro`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        codigoOperador: Number(codigoOperador),
        dataUltimaAlteracao: data,
      }),
    });
    if (!res.ok) return null;

    const dados = await res.json();
    const lista = Array.isArray(dados) ? dados : (dados?.tickets || dados?.dados || []);
    if (!Array.isArray(lista) || !lista.length) return null;

    // A leitura do instante (com o fuso da empresa, e não o da máquina) mora em
    // services/ociosidade.js, junto do resto da régua — é lá que ela pode ser
    // conferida sem rede.
    return alteracaoMaisRecente(lista);
  } catch (err) {
    console.warn('[Orion] Última alteração do operador:', err.message);
    return null;
  }
}

/**
 * Tickets do operador na FILA DE RETORNO (status 5) — os que têm hora marcada
 * para voltar ao cliente.
 *
 * Mesmo endpoint do contador de "em atendimento", trocando o status. A
 * diferença é que aqui interessa a LISTA, não a contagem: cada ticket carrega
 * o horário do compromisso, e é isso que a tela mostra e o aviso usa.
 *
 * Devolve `null` (e não lista vazia) quando a consulta falha. São coisas
 * diferentes: vazio significa "não há retorno agendado" e a tela pode dizer
 * isso com segurança; null significa "não sei", e aí o card mantém o que já
 * tinha em vez de anunciar que a agenda está limpa.
 */
export async function ticketsFilaRetorno(codigoOperador) {
  return ticketsPorStatus(codigoOperador, STATUS_FILA_RETORNO);
}

/**
 * Números dos tickets em atendimento NO NOME do operador.
 *
 * É a lista que acompanha a notificação de "em atendimento" ao n8n. O filtro é
 * o mesmo do 1.4.1, campo por campo — `codigoStatusTicket` e `codigoSetorTicket`
 * (e não `codigoStatus`/`codigoSetor`, que a mesma resposta também traz): status
 * 36, setor 3, operador atual.
 *
 * Lista vazia quando não dá para saber. A notificação é acessória e não pode
 * derrubar a ação que acabou de dar certo.
 */
export async function ticketsEmAtendimentoDoOperador(codigoOperador) {
  return (await pendentesEmAtendimentoDoOperador(codigoOperador)) || [];
}

/**
 * O mesmo, distinguindo "não tem" de "não deu para saber".
 *
 * Devolve a lista de números, ou `null` quando a consulta falhou. Quem decide
 * ociosidade precisa dessa diferença: lista vazia é informação ("não está
 * atendendo"), `null` não é ("a rede caiu"), e tratar um como o outro marca
 * ocioso quem só teve azar de conexão.
 */
export async function pendentesEmAtendimentoDoOperador(codigoOperador) {
  if (!codigoOperador) return null;
  try {
    const res = await fetch(`${MYSUITE_API_BASE}/dashboard/suporte/ticket/pendentes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    if (!res.ok) throw new Error(`Erro ${res.status}`);
    const tickets = await res.json();
    if (!Array.isArray(tickets)) return null;
    return tickets
      .filter(t => t.codigoStatusTicket === STATUS_EM_ATENDIMENTO
        && t.codigoSetorTicket === SETOR_SUPORTE
        && t.codigoOperador === Number(codigoOperador))
      .map(t => t.codigo)
      .filter(c => c != null);
  } catch (err) {
    console.warn('[Orion] Falha ao obter tickets em atendimento:', err.message);
    return null;
  }
}

/**
 * O operador está atendendo AGORA?
 *
 * ─────────────────────────────────────────────────────────────────────────
 * DUAS FONTES, E NÃO UMA. Foi medido contra a API, no mesmo instante, e as
 * duas divergem — cada uma para um lado:
 *
 *   · `/tickets/filtro` com status 36 é a leitura mais fresca, mas não vê
 *     tudo. Havia ticket em "SUP - Em Atendimento" que só ele trazia;
 *   · a fila de pendentes vê tickets que o filtro não trouxe, mas ATRASA:
 *     chegou a devolver como 36 um ticket que já estava concluído havia
 *     nove minutos.
 *
 * Usar só uma delas é escolher qual erro cometer. Aqui elas entram em OU:
 * basta uma dizer que há atendimento. Para a ociosidade esse é o lado certo
 * de errar — o preço de um falso "está atendendo" são alguns minutos a menos
 * de ócio apurado; o preço de um falso "não está" é registrar ociosidade de
 * quem estava trabalhando.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Devolve `true`, `false`, ou `null` quando NENHUMA das duas respondeu — e aí
 * quem chama mantém o que já sabia.
 */
export async function operadorEstaAtendendo(codigoOperador) {
  if (!codigoOperador) return null;

  const [quantos, pendentes] = await Promise.all([
    quantosEmAtendimento(codigoOperador),
    pendentesEmAtendimentoDoOperador(codigoOperador),
  ]);

  return decidirEmAtendimento(quantos, pendentes);
}

/** Valida um número de ticket e devolve { valido, titulo, cnpj }. */
export async function validarTicket(numero) {
  const num = String(numero || '').replace(/\D/g, '');
  if (!num) return { valido: false, mensagem: 'Informe o número do ticket.' };
  try {
    const data = await buscarTicket(num);
    if (!data) return { valido: false, mensagem: 'Ticket não encontrado.' };
    return {
      valido: true,
      titulo: data.titulo || '',
      cnpj: data.cnpj || data.cnpjCliente || '',
      dados: data,
    };
  } catch (err) {
    return { valido: false, mensagem: err.message || 'Ticket não encontrado.' };
  }
}
